/**
 * 路由表（架构 §2 src/routes/AppRoutes.tsx / T03 / T10 / T12）
 *  - /login /register 公开
 *  - / 受保护，布局含 Sidebar + TopBar + Outlet
 *      /           会话列表
 *      /user       用户中心
 *      /invites    邀请码管理（创始人）
 *      /c/:id      聊天页（私聊/群聊）
 *
 *  U2 §A3 性能分包：受保护区的页面级组件经 React.lazy 拆分，首屏仅加载会话列表，
 *  聊天页 / 用户中心等进入时按需加载（对应 chunk）。
 */
import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import useMediaQuery from '@mui/material/useMediaQuery';
import ProtectedRoute from './ProtectedRoute';
import Sidebar from '../components/layout/Sidebar';
import TopBar from '../components/layout/TopBar';
import LoginPage from '../pages/LoginPage';
import RegisterPage from '../pages/RegisterPage';
import { useAuthStore } from '../store/auth.store';
import { useUserStore } from '../store/user.store';
import { useChatStore } from '../store/chat.store';
import { useArtifactStore } from '../store/artifact.store';
import { getConversations } from '../api/conversation.api';
import { get } from '../api/client';
import type { MeResponse } from '../types/models';
import { socketManager } from '../socket/client';

// 页面级懒加载（首屏分包）
const UserCenterPage = lazy(() => import('../pages/UserCenterPage'));
const ConversationListPage = lazy(() => import('../pages/ConversationListPage'));
const ChatPage = lazy(() => import('../pages/ChatPage'));
const InviteManagePage = lazy(() => import('../pages/InviteManagePage'));

/** 受保护区统一布局：加载 profile + 会话列表，并按会话批量加入实时房间 */
export function RouteLoadingFallback() {
  return (
    <Box
      role="status"
      aria-label={'\u6b63\u5728\u52a0\u8f7d\u9875\u9762'}
      sx={{ p: { xs: 2, md: 3 }, height: '100%', overflow: 'hidden' }}
    >
      <Skeleton variant="text" width={180} height={38} sx={{ mb: 2 }} />
      <Skeleton variant="rounded" height={136} sx={{ mb: 3, borderRadius: 3 }} />
      <Box
        data-testid="route-loading-grid"
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(4, minmax(0, 1fr))',
          },
        }}
      >
        {[0, 1, 2, 3].map((item) => (
          <Skeleton key={item} variant="rounded" height={240} sx={{ borderRadius: 3 }} />
        ))}
      </Box>
    </Box>
  );
}

function Layout() {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const setProfile = useUserStore((s) => s.setProfile);
  const setConversations = useChatStore((s) => s.setConversations);
  const resetChat = useChatStore((s) => s.reset);
  const isMobile = useMediaQuery('(max-width:768px)');
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const mainRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    (async () => {
      try {
        // 当前用户 + 我的产出（含 stats）
        const me = await get<MeResponse>('/user/me');
        if (cancelled) return;
        setProfile(me.user, me.myFiles ?? []);
        // 会话列表
        const list = await getConversations();
        if (cancelled) return;
        setConversations(list);
      } catch {
        /* 拦截器已处理 1001/1002 跳登录 */
      }
    })();

    return () => {
      cancelled = true;
    };
    // 仅在 token 变化时重新加载
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // 登出时清理实时连接
  useEffect(() => {
    if (!token) {
      resetChat();
      useArtifactStore.getState().reset();
      socketManager.disconnectAll();
    }
  }, [token, resetChat]);

  // 监听鉴权失效（socket error 1001/1002 或 axios 拦截触发）
  useEffect(() => {
    function onExpired() {
      navigate('/login', { replace: true });
    }
    window.addEventListener('zhihui:auth-expired', onExpired);
    return () => window.removeEventListener('zhihui:auth-expired', onExpired);
  }, [navigate]);
  useEffect(() => {
    mainRef.current?.focus({ preventScroll: true });
  }, [location.pathname]);


  return (
    <Box sx={{ display: 'flex', height: '100dvh', bgcolor: 'background.default' }}>
      <Box
        component="a"
        href="#main-content"
        sx={{ position: 'fixed', top: 8, left: 8, zIndex: 2000, px: 2, py: 1, bgcolor: 'background.paper', color: 'primary.main', borderRadius: 1, transform: 'translateY(-160%)', '&:focus': { transform: 'translateY(0)' } }}
      >
        {'\u8df3\u5230\u4e3b\u8981\u5185\u5bb9'}
      </Box>
      <Sidebar
        isMobile={isMobile}
        mobileOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
      />
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <TopBar
          showMenu={isMobile}
          onMenuClick={() => setMobileOpen((v) => !v)}
        />
        <Box component="main" id="main-content" ref={mainRef} tabIndex={-1} sx={{ flex: 1, minHeight: 0, outline: 'none' }}>
          <Suspense fallback={<RouteLoadingFallback />}>
            <Outlet />
          </Suspense>
        </Box>
      </Box>
    </Box>
  );
}

export default function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<ConversationListPage />} />
        <Route path="user" element={<UserCenterPage />} />
        <Route path="invites" element={<InviteManagePage />} />
        <Route path="c/:conversationId" element={<ChatPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
